import { IconButton, Paper, Stack } from '@mui/material';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface DraggableWindowProps {
  children: ReactNode;
  onClose: () => void;
  initialPosition?: Position;
  initialSize?: Size;
  title?: string;
  url?: string;
  maxWidth?: string;
  maxHeight?: string;
  adjustable?: boolean;
}

export default function DraggableWindow({
  children,
  onClose,
  initialPosition = { x: 50, y: 20 },
  initialSize = { width: 800, height: 1600 },
  title,
  maxWidth = '80vw',
  maxHeight = '90vh',
  adjustable = true,
  url
}: DraggableWindowProps) {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [size, setSize] = useState<Size>(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState<Position>({ x: 0, y: 0 });
  const windowRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const minWidth = 300;
  const minHeight = 200;
  const maxWidthNum = parseFloat(maxWidth) * window.innerWidth / 100;
  const maxHeightNum = parseFloat(maxHeight) * window.innerHeight / 100;

  const handleMouseDown = (e: MouseEvent) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleResizeStart = (e: MouseEvent) => {
    if (!adjustable || isMobile) return;
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
    });
    e.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (windowRef.current) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const windowRect = windowRef.current.getBoundingClientRect();

        if (isDragging) {
          let newX = e.clientX - dragOffset.x;
          let newY = e.clientY - dragOffset.y;

          const minX = 0;
          const maxX = viewportWidth - windowRect.width;
          newX = Math.max(minX, Math.min(newX, maxX));

          const minY = 0;
          const maxY = viewportHeight - windowRect.height;
          newY = Math.max(minY, Math.min(newY, maxY));

          setPosition({ x: newX, y: newY });
        }

        if (isResizing && adjustable && !isMobile) {
          const deltaX = e.clientX - resizeStart.x;
          const deltaY = e.clientY - resizeStart.y;

          const maxAllowedWidth = Math.min(maxWidthNum, viewportWidth - position.x);
          const maxAllowedHeight = Math.min(maxHeightNum, viewportHeight - position.y);

          const newWidth = Math.max(minWidth, Math.min(size.width + deltaX, maxAllowedWidth));
          const newHeight = Math.max(minHeight, Math.min(size.height + deltaY, maxAllowedHeight));

          setSize({ width: newWidth, height: newHeight });
          setResizeStart({ x: e.clientX, y: e.clientY });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeStart, size, position, adjustable, maxWidthNum, maxHeightNum, isMobile]);

  // Ensure initial position and size are within bounds
  useEffect(() => {
    if (windowRef.current) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newX = position.x;
      let newY = position.y;
      let newWidth = size.width;
      let newHeight = size.height;

      newWidth = Math.max(minWidth, Math.min(newWidth, maxWidthNum, viewportWidth - newX));
      newHeight = Math.max(minHeight, Math.min(newHeight, maxHeightNum, viewportHeight - newY));

      const minX = 0;
      const maxX = viewportWidth - newWidth;
      newX = Math.max(minX, Math.min(newX, maxX));

      const minY = 0;
      const maxY = viewportHeight - newHeight;
      newY = Math.max(minY, Math.min(newY, maxY));

      if (newX !== position.x || newY !== position.y || newWidth !== size.width || newHeight !== size.height) {
        setPosition({ x: newX, y: newY });
        setSize({ width: newWidth, height: newHeight });
      }
    }
  }, [position.x, position.y, size.width, size.height, maxWidthNum, maxHeightNum]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Paper
      ref={windowRef}
      sx={{
        position: 'fixed',
        left: position.x,
        borderRadius: .5,
        top: position.y,
        zIndex: 1300,
        width: isMobile ? '100%' : size.width,
        height: isMobile ? '100%' : size.height,
        minWidth: isMobile ? '100%' : minWidth,
        minHeight: isMobile ? '100%' : minHeight,
        maxWidth: isMobile ? '100%' : maxWidth,
        maxHeight: isMobile ? '100%' : maxHeight,
        overflowY: 'hidden',
        overflowX: 'hidden',
        boxShadow: 24,
        resize: 'none', // Disable native resizing
      }}
      elevation={24}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          userSelect: 'none'
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1,
            cursor: isDragging ? 'grabbing' : 'grab',
            bgcolor: 'background.neutral',
            userSelect: 'none',
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 1000
          }}
          onMouseDown={handleMouseDown}
        >
          <TruncatedTextField
            variant="subtitle2"
            text={title}
            limit={50}
            mode='characters'
            showMoreEnabled={false}
          />
          <Stack direction="row" spacing={1} sx={{ zIndex: 1000 }}>
            {url && (
              <IconButton
                size="small"
                onClick={() => window.open(url, '_blank')}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Iconify icon="mingcute:external-link-line" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleClose}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Iconify icon="mi:close" />
            </IconButton>
          </Stack>
        </Stack>

        <Box pt={2} width="100%" sx={{ height: 'calc(100% - 20px)', overflowY: 'auto', padding: 1, }}>
          {children}
        </Box>

        {adjustable && !isMobile && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 16,
              height: 16,
              cursor: 'se-resize',
              borderRight: '3px solid',
              borderBottom: '3px solid',
              borderColor: 'grey.500',
              zIndex: 1000,
            }}
            onMouseDown={handleResizeStart}
          />
        )}
      </Box>
    </Paper>
  );
}